import React, { useState, useEffect, useMemo } from "react";
import { authFetch } from "../pages/Auth";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  COLORS, CHART_COLORS, RISK_COLORS, commonAxisStyle, commonGridStyle,
  tooltipStyle, cardStyle, chartHeaderStyle,
} from "../chartTheme";

// ═══════════════════════════════════════════════════════════
// DATA HOOKS — with request dedup + 5-min cache
// ═══════════════════════════════════════════════════════════

const _cache = {};   // path → { data, ts }
const _inflight = {}; // path → Promise<data>
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function useApi(path, { lazy = false } = {}) {
  const cached = _cache[path];
  const isFresh = cached && (Date.now() - cached.ts < CACHE_TTL);

  const [data, setData] = useState(isFresh ? cached.data : null);
  const [loading, setLoading] = useState(!lazy && !isFresh);
  const [error, setError] = useState(null);
  const [triggered, setTriggered] = useState(!lazy);

  useEffect(() => {
    if (!triggered) return;

    // Serve from cache if fresh
    if (_cache[path] && Date.now() - _cache[path].ts < CACHE_TTL) {
      setData(_cache[path].data);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    // Dedup: reuse in-flight promise for the same path
    if (!_inflight[path]) {
      _inflight[path] = authFetch(path)
        .then((r) => r.json())
        .then((d) => {
          _cache[path] = { data: d, ts: Date.now() };
          return d;
        })
        .finally(() => { delete _inflight[path]; });
    }

    _inflight[path]
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });

    return () => { cancelled = true; };
  }, [path, triggered]);

  const trigger = () => setTriggered(true);
  return { data, loading, error, trigger, triggered };
}

// ═══════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%",
        border: `3px solid ${COLORS.border}`,
        borderTopColor: COLORS.primary,
        animation: "spin 0.8s linear infinite",
      }} />
    </div>
  );
}

function ErrorBox({ message, onRetry }) {
  return (
    <div style={{ textAlign: "center", padding: 30 }}>
      <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 8 }}>{message}</div>
      {onRetry && (
        <button onClick={onRetry} style={{
          background: COLORS.bg, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, color: COLORS.textDim, padding: "6px 16px",
          fontSize: 11, cursor: "pointer",
        }}>Retry</button>
      )}
    </div>
  );
}

function KPICard({ label, value, sub, color, icon }) {
  return (
    <div style={{
      ...cardStyle,
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: color || COLORS.primary,
        borderRadius: "10px 10px 0 0",
      }} />
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 8,
      }}>
        <span style={{
          fontSize: 10, color: COLORS.muted, letterSpacing: 1,
          textTransform: "uppercase",
        }}>{label}</span>
        {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
      </div>
      <div style={{
        fontSize: 28, fontWeight: 800,
        color: color || COLORS.text, lineHeight: 1, marginBottom: 4,
      }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.textDim }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, loading: isLoading, error: err }) {
  return (
    <div style={cardStyle}>
      <div style={{ marginBottom: 16 }}>
        <div style={chartHeaderStyle}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 10, color: COLORS.muted, fontFamily: "monospace", letterSpacing: 1 }}>
            {subtitle}
          </div>
        )}
      </div>
      {isLoading ? <Spinner /> : err ? <ErrorBox message={err} /> : children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CHART COMPONENTS
// ═══════════════════════════════════════════════════════════

function PopulationWeightChart() {
  const { data, loading, error } = useApi("/api/analytics/weight-trends");

  const trends = Array.isArray(data?.trends) ? data.trends : [];
  const chartData = [...trends].reverse().map((t) => ({
    month: typeof t.month === "string" ? t.month.slice(0, 7) : String(t.month ?? ""),
    avg: t.avg_weight ?? 0,
    min: t.min_weight ?? 0,
    max: t.max_weight ?? 0,
    patients: t.patients_measured ?? 0,
  }));

  return (
    <ChartCard title="Population Weight Trend" subtitle="MONTHLY AVG FROM BIGQUERY" loading={loading} error={error}>
      {chartData.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>No weight data available</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="weightGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.2} />
                <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...commonGridStyle} />
            <XAxis dataKey="month" tick={commonAxisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={commonAxisStyle} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
            <Tooltip {...tooltipStyle} formatter={(v, name) => [`${v} lbs`, name === "avg" ? "Avg Weight" : name]} />
            <Area type="monotone" dataKey="avg" stroke={COLORS.primary} strokeWidth={2} fill="url(#weightGrad)" name="Avg Weight" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function RiskDistributionChart() {
  const { data, loading, error } = useApi("/api/ml-cache/dropout-risk");

  const dist = (data?.risk_distribution && typeof data.risk_distribution === "object") ? data.risk_distribution : {};
  const chartData = Object.entries(dist).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value: value ?? 0,
    color: RISK_COLORS[name] || COLORS.muted,
  }));

  return (
    <ChartCard title="Dropout Risk Distribution" subtitle="ML MODEL PREDICTIONS" loading={loading} error={error}>
      {chartData.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>Train ML models first</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={chartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
              dataKey="value" nameKey="name" paddingAngle={4}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.color} stroke="none" />
              ))}
            </Pie>
            <Tooltip {...tooltipStyle} formatter={(v, name) => [v, `${name} Risk`]} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 11, color: COLORS.textDim }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function SegmentChart() {
  const { data, loading, error } = useApi("/api/ml-cache/segments");

  const segDist = (data?.segment_distribution && typeof data.segment_distribution === "object") ? data.segment_distribution : {};
  const chartData = Object.entries(segDist).map(([name, value], i) => ({
    name: `Segment ${name}`,
    patients: value ?? 0,
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  return (
    <ChartCard title="Patient Segments" subtitle="K-MEANS CLUSTERING" loading={loading} error={error}>
      {chartData.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>No segment data</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData}>
            <CartesianGrid {...commonGridStyle} vertical={false} />
            <XAxis dataKey="name" tick={commonAxisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={commonAxisStyle} axisLine={false} tickLine={false} />
            <Tooltip {...tooltipStyle} formatter={(v) => [v, "Patients"]} />
            <Bar dataKey="patients" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function SurvivalChart() {
  const { data, loading, error, trigger, triggered } = useApi("/api/ml/survival", { lazy: true });

  const curve = Array.isArray(data?.survival_curve) ? data.survival_curve : [];
  const chartData = curve.map((pt) => ({
    day: pt.time_days ?? pt.time ?? pt.day ?? 0,
    probability: Math.round(((pt.survival_probability ?? pt.survival ?? pt.probability ?? 0)) * 100),
  }));

  if (!triggered) {
    return (
      <ChartCard title="Program Retention Curve" subtitle="KAPLAN-MEIER SURVIVAL">
        <div style={{ textAlign: "center", padding: 30 }}>
          <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 12 }}>Survival analysis requires ML computation</div>
          <button onClick={trigger} style={{
            padding: "8px 20px", background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.3)",
            borderRadius: 6, color: COLORS.primary, fontSize: 11, cursor: "pointer", fontWeight: 600,
          }}>Load Analysis</button>
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title="Program Retention Curve" subtitle="KAPLAN-MEIER SURVIVAL" loading={loading} error={error}>
      {chartData.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>
          {data?.median_survival_days ? `Median retention: ${data.median_survival_days} days` : "No survival data"}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="survGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.2} />
                <stop offset="95%" stopColor={COLORS.success} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...commonGridStyle} />
            <XAxis dataKey="day" tick={commonAxisStyle} axisLine={false} tickLine={false} label={{ value: "Days", position: "insideBottom", offset: -5, style: commonAxisStyle }} />
            <YAxis tick={commonAxisStyle} axisLine={false} tickLine={false} domain={[0, 100]} />
            <Tooltip {...tooltipStyle} formatter={(v) => [`${v}%`, "Retention"]} />
            <Area type="stepAfter" dataKey="probability" stroke={COLORS.success} strokeWidth={2} fill="url(#survGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function AdherenceChart() {
  const { data, loading, error } = useApi("/api/analytics/adherence");

  const chartData = (data?.adherence || []).map((r) => ({
    program: (r.program_name || "Unknown").split(" ")[0],
    patients: r.patient_count,
    pdc: Math.round((r.pdc_above_80_pct || 0) * 100),
    refills: r.avg_refills,
  }));

  return (
    <ChartCard title="Adherence by Program" subtitle="PDC >= 80% RATE" loading={loading} error={error}>
      {chartData.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>No adherence data</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData}>
            <CartesianGrid {...commonGridStyle} vertical={false} />
            <XAxis dataKey="program" tick={commonAxisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={commonAxisStyle} axisLine={false} tickLine={false} domain={[0, 100]} />
            <Tooltip {...tooltipStyle} formatter={(v, name) => [name === "pdc" ? `${v}%` : v, name === "pdc" ? "PDC >=80%" : "Patients"]} />
            <Bar dataKey="pdc" fill={COLORS.secondary} radius={[4, 4, 0, 0]} name="PDC >=80%" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function AnomalyAlerts() {
  const { data, loading, error, trigger, triggered } = useApi("/api/ml/anomalies?severity=high", { lazy: true });

  const anomalies = data?.anomalies || [];

  if (!triggered) {
    return (
      <ChartCard title="Clinical Anomaly Alerts" subtitle="HIGH SEVERITY">
        <div style={{ textAlign: "center", padding: 30 }}>
          <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 12 }}>Anomaly detection requires ML computation</div>
          <button onClick={trigger} style={{
            padding: "8px 20px", background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.3)",
            borderRadius: 6, color: COLORS.primary, fontSize: 11, cursor: "pointer", fontWeight: 600,
          }}>Run Detection</button>
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title="Clinical Anomaly Alerts" subtitle="HIGH SEVERITY" loading={loading} error={error}>
      {anomalies.length === 0 ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          color: COLORS.success, fontSize: 12, padding: 10,
        }}>
          <span style={{ fontSize: 16 }}>&#10003;</span> No high-severity anomalies detected
        </div>
      ) : (
        <div style={{ maxHeight: 200, overflowY: "auto" }}>
          {anomalies.slice(0, 5).map((a, i) => (
            <div key={i} style={{
              padding: "8px 12px", borderRadius: 6, marginBottom: 6,
              background: "rgba(255,77,109,0.06)",
              border: "1px solid rgba(255,77,109,0.15)",
              fontSize: 11, color: COLORS.text,
            }}>
              <span style={{ color: COLORS.danger, fontWeight: 700 }}>
                {a.patient_id?.slice(0, 12)}
              </span>
              {" — "}{a.description || a.anomaly_type || "Anomaly detected"}
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

function ROICard({ summaryData, adherenceData, loading, error }) {
  const adherence = Array.isArray(adherenceData?.adherence) ? adherenceData.adherence : [];
  const totalAdh = adherence.reduce((s, r) => s + (r.patient_count || 0), 0);
  const adherentCount = adherence.reduce((s, r) => s + Math.round((r.patient_count || 0) * (r.pdc_above_80_pct || 0)), 0);
  const annualSavings = adherentCount > 0 ? adherentCount * 4200 : null;

  return (
    <ChartCard title="Projected ROI" subtitle="BASED ON ADHERENT PATIENTS" loading={loading} error={error}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{
          padding: 14, borderRadius: 8,
          background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.15)",
        }}>
          <div style={{ fontSize: 10, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
            ADHERENT PATIENTS
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.success }}>
            {adherentCount > 0 ? adherentCount.toLocaleString() : "\u2014"}
          </div>
          <div style={{ fontSize: 9, color: COLORS.muted }}>PDC &ge; 80%</div>
        </div>
        <div style={{
          padding: 14, borderRadius: 8,
          background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.15)",
        }}>
          <div style={{ fontSize: 10, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
            EST. ANNUAL SAVINGS
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.primary }}>
            {annualSavings != null ? `$${annualSavings.toLocaleString()}` : "\u2014"}
          </div>
          <div style={{ fontSize: 9, color: COLORS.muted }}>$4,200/yr per adherent patient</div>
        </div>
      </div>
    </ChartCard>
  );
}

function MilestoneCards() {
  const { data, loading, error, trigger, triggered } = useApi("/api/ml/weight-forecast", { lazy: true });

  const milestones = [
    { pct: 5,  label: "5% Weight Loss",  color: COLORS.info },
    { pct: 10, label: "10% Weight Loss", color: COLORS.success },
    { pct: 15, label: "15% Weight Loss", color: COLORS.secondary },
  ];

  const summary = data?.population_summary || data?.summary || {};

  if (!triggered) {
    return (
      <ChartCard title="Weight Loss Milestones" subtitle="POPULATION ACHIEVEMENT">
        <div style={{ textAlign: "center", padding: 30 }}>
          <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 12 }}>Requires ML weight forecasting</div>
          <button onClick={trigger} style={{
            padding: "8px 20px", background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.3)",
            borderRadius: 6, color: COLORS.primary, fontSize: 11, cursor: "pointer", fontWeight: 600,
          }}>Load Forecast</button>
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title="Weight Loss Milestones" subtitle="POPULATION ACHIEVEMENT" loading={loading} error={error}>
      <div style={{ display: "flex", gap: 10 }}>
        {milestones.map((m) => {
          const key = `pct_${m.pct}_reached`;
          const count = summary[key] ?? summary[`reached_${m.pct}`] ?? null;
          return (
            <div key={m.pct} style={{
              flex: 1, padding: 12, borderRadius: 8, textAlign: "center",
              background: `${m.color}08`, border: `1px solid ${m.color}20`,
            }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: m.color, marginBottom: 4 }}>
                {count != null ? count : "\u2014"}
              </div>
              <div style={{ fontSize: 10, color: COLORS.muted }}>{m.label}</div>
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
}

function ComorbidityChart({ summaryData, loading, error }) {
  const summary = summaryData?.summary || {};
  const chartData = useMemo(() => {
    // Derive from summary patient counts if comorbidity breakdown exists
    const comorbidity = summaryData?.comorbidity_outcomes || summaryData?.comorbidity || null;
    if (comorbidity) {
      return Object.entries(comorbidity).map(([name, val]) => ({
        name: name.replace(/_/g, " ").replace(/has /i, ""),
        value: typeof val === "object" ? (val.avg_weight_loss ?? val.count ?? 0) : val,
      }));
    }
    // Fallback: show comorbidity prevalence from summary if available
    const fields = [
      { key: "t2d_count", name: "Type 2 DM" },
      { key: "hypertension_count", name: "Hypertension" },
      { key: "hyperlipidemia_count", name: "Hyperlipidemia" },
      { key: "sleep_apnea_count", name: "Sleep Apnea" },
      { key: "cardiovascular_count", name: "Cardiovascular" },
    ];
    const result = fields
      .map((f) => ({ name: f.name, value: summary[f.key] ?? 0 }))
      .filter((d) => d.value > 0);
    return result.length > 0 ? result : [
      { name: "Type 2 DM", value: 0 },
      { name: "Hypertension", value: 0 },
      { name: "Hyperlipidemia", value: 0 },
    ];
  }, [summaryData, summary]);

  return (
    <ChartCard title="Outcomes by Comorbidity" subtitle="PATIENT PREVALENCE" loading={loading} error={error}>
      {chartData.every((d) => d.value === 0) ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>
          Comorbidity data available after ML training
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid {...commonGridStyle} horizontal={false} />
            <XAxis type="number" tick={commonAxisStyle} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" tick={commonAxisStyle} axisLine={false} tickLine={false} width={100} />
            <Tooltip {...tooltipStyle} />
            <Bar dataKey="value" fill={COLORS.primary} radius={[0, 4, 4, 0]} barSize={16} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════

export default function DashboardHome() {
  const { data: summaryData, loading: summaryLoading, error: summaryError } = useApi("/api/analytics/summary");
  const { data: riskData } = useApi("/api/ml-cache/dropout-risk");  // cached — instant
  const { data: adherenceData } = useApi("/api/analytics/adherence");

  const summary = summaryData?.summary || {};
  const riskDist = riskData?.risk_distribution || {};
  const highRisk = riskDist.high || 0;
  const mediumRisk = riskDist.medium || 0;
  const totalPatients = summary.total_patients || 0;
  const activePatients = summary.active_patients || 0;
  const inactivePatients = summary.inactive_patients || 0;
  const retentionRate = summary.retention_rate;

  // Adherence metrics
  const adherence = Array.isArray(adherenceData?.adherence) ? adherenceData.adherence : [];
  const totalAdherencePatients = adherence.reduce((s, r) => s + (r.patient_count || 0), 0);
  const adherentPatients = adherence.reduce((s, r) => s + Math.round((r.patient_count || 0) * (r.pdc_above_80_pct || 0)), 0);
  const avgPDC = totalAdherencePatients > 0 ? Math.round((adherentPatients / totalAdherencePatients) * 100) : null;
  const avgRefills = adherence.length > 0 ? (adherence.reduce((s, r) => s + (r.avg_refills || 0), 0) / adherence.length).toFixed(1) : null;

  // ML metrics
  const modelAUC = riskData?.model_metrics?.auc;
  const estMonthlySavings = adherentPatients > 0 ? Math.round(adherentPatients * 4200 / 12) : null;

  return (
    <div>
      <style>{`
        .ph-grid-4{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px}
        .ph-grid-2w{display:grid;grid-template-columns:1.6fr 1fr;gap:16px;margin-bottom:16px}
        .ph-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px}
        .ph-grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;margin-bottom:16px}
        @media(max-width:1024px){
          .ph-grid-4{grid-template-columns:repeat(2,1fr)}
          .ph-grid-2w,.ph-grid-2,.ph-grid-3{grid-template-columns:1fr}
        }
        @media(max-width:640px){
          .ph-grid-4{grid-template-columns:1fr}
        }
      `}</style>

      {/* KPI Cards */}
      <div className="ph-grid-4">
        <KPICard
          label="Total Patients"
          value={summaryLoading ? "..." : totalPatients.toLocaleString()}
          sub="Enrolled in program"
          color={COLORS.primary}
          icon="&#128101;"
        />
        <KPICard
          label="Active Patients"
          value={summaryLoading ? "..." : activePatients.toLocaleString()}
          sub="Currently on GLP-1"
          color={COLORS.success}
          icon="&#10003;"
        />
        <KPICard
          label="Retention Rate"
          value={summaryLoading ? "..." : retentionRate != null ? `${(retentionRate * 100).toFixed(1)}%` : "\u2014"}
          sub="Active / Total"
          color={COLORS.info}
          icon="&#128200;"
        />
        <KPICard
          label="High Risk Patients"
          value={highRisk}
          sub="Need intervention"
          color={COLORS.danger}
          icon="&#9888;"
        />
      </div>

      {/* Clinical & Operational KPIs */}
      <div className="ph-grid-4" style={{ marginBottom: 20 }}>
        <KPICard
          label="PDC Adherence"
          value={avgPDC != null ? `${avgPDC}%` : "\u2014"}
          sub={adherentPatients > 0 ? `${adherentPatients} of ${totalAdherencePatients} adherent` : "PDC \u2265 80%"}
          color="#60a5fa"
        />
        <KPICard
          label="Avg Refills"
          value={avgRefills || "\u2014"}
          sub="Per patient"
          color={COLORS.secondary}
        />
        <KPICard
          label="Medium Risk"
          value={mediumRisk}
          sub="Monitor closely"
          color={COLORS.warning}
          icon="&#9888;"
        />
        <KPICard
          label="Est. Monthly Savings"
          value={estMonthlySavings != null ? `$${estMonthlySavings.toLocaleString()}` : "\u2014"}
          sub="$4,200/yr per adherent patient"
          color={COLORS.success}
        />
      </div>

      {/* Row 1: Weight + Risk */}
      <div className="ph-grid-2w">
        <PopulationWeightChart />
        <RiskDistributionChart />
      </div>

      {/* Row 2: Segments + Survival */}
      <div className="ph-grid-2">
        <SegmentChart />
        <SurvivalChart />
      </div>

      {/* Row 3: Adherence + Anomalies */}
      <div className="ph-grid-2">
        <AdherenceChart />
        <AnomalyAlerts />
      </div>

      {/* Row 4: ROI + Milestones + Comorbidity */}
      <div className="ph-grid-3">
        <ROICard summaryData={summaryData} adherenceData={adherenceData} loading={summaryLoading} error={summaryError} />
        <MilestoneCards />
        <ComorbidityChart summaryData={summaryData} loading={summaryLoading} error={summaryError} />
      </div>
    </div>
  );
}
